/** Human-readable summaries for page_configuration objects (permissions table + role templates). */

function getUserProfileViewLayoutFromConfig(cfg) {
  if (!cfg || typeof cfg !== 'object') return null;
  if (cfg.user_profile_view_config && Array.isArray(cfg.user_profile_view_config.columns)) {
    return cfg.user_profile_view_config;
  }
  if (Array.isArray(cfg.columns)) return { columns: cfg.columns };
  return null;
}

/**
 * @param {object|null|undefined} cfg
 * @param {{ pageName?: string }} [options]
 * @returns {string}
 */
export function summarizePageConfiguration(cfg, { pageName } = {}) {
  const pipelineSlots = Array.isArray(cfg)
    ? cfg
    : Array.isArray(cfg?.slots)
      ? cfg.slots
      : null;

  if (
    (!cfg || typeof cfg !== 'object' || Object.keys(cfg).length === 0) &&
    !pipelineSlots
  ) {
    if (pageName === 'user-profiles') {
      return 'page access (read-only defaults)';
    }
    if (pageName === 'profile') {
      return 'page access (read-only defaults)';
    }
    if (pageName === 'scheduler') {
      return 'page access (view only — no create/edit/remove flags)';
    }
    if (pageName === 'forms') {
      return 'page access (view only — no forms action flags)';
    }
    if (pageName === 'work-samples') {
      return 'page access (view only — no work sample action flags)';
    }
    if (pageName === 'inquiries') {
      return 'page access (view list — no notify)';
    }
    return 'page access only';
  }

  const parts = [];
  const obj = Array.isArray(cfg) ? {} : cfg;
  if (obj.widgets?.length) parts.push(`widgets: [${obj.widgets.join(', ')}]`);
  if (Array.isArray(pipelineSlots) && pipelineSlots.length) {
    const required = pipelineSlots.filter((s) => s && s.required).map((s) => s.key || s.label);
    const optional = pipelineSlots.filter((s) => s && !s.required).map((s) => s.key || s.label);
    const bits = [];
    if (required.length) bits.push(`required: [${required.join(', ')}]`);
    if (optional.length) bits.push(`optional: [${optional.join(', ')}]`);
    parts.push(bits.length ? `pipeline (${bits.join('; ')})` : `pipeline: ${pipelineSlots.length} slot(s)`);
  }
  if (Array.isArray(cfg.items)) {
    const countNested = (items) =>
      (items || []).reduce(
        (n, it) => n + 1 + countNested(it.children),
        0
      );
    const n = countNested(cfg.items);
    parts.push(n ? `sidebar: ${n} item(s)` : 'sidebar: empty');
  }
  if (cfg.view) parts.push(`view: ${cfg.view}`);
  if (cfg.role_group_name_includes?.length) {
    parts.push(`role_group_name_includes: [${cfg.role_group_name_includes.join(', ')}]`);
  }
  if (cfg.role_group_name_excludes?.length) {
    parts.push(`role_group_name_excludes: [${cfg.role_group_name_excludes.join(', ')}]`);
  }
  if (cfg.role_group_id_excludes?.length) {
    parts.push(`role_group_id_excludes: ${cfg.role_group_id_excludes.length} team(s)`);
  }
  if (cfg.can_create_entities === true) parts.push('can_create_entities: true');
  else if (cfg.can_create_entities === false) parts.push('can_create_entities: false');
  if (cfg.can_create_organizations === true) parts.push('can_create_organizations: true');
  if (cfg.create_entities_in_own_group === true) parts.push('create_entities_in_own_group: true');
  if (cfg.create_entities_same_level === true) parts.push('create_entities_same_level: true');
  if (cfg.create_entities_org_wide === true) parts.push('create_entities_org_wide: true');
  if (cfg.can_add_members === true) parts.push('can_add_members: true');
  else if (cfg.can_add_members === false) parts.push('can_add_members: false');
  if (cfg.see_all_same_level_children === true) parts.push('see_all_same_level_children: true');
  if (cfg.add_members_to_own_group === true) parts.push('add_members_to_own_group: true');
  else if (cfg.add_members_to_own_group === false) parts.push('add_members_to_own_group: false');
  if (cfg.can_add_members_same_level === true) parts.push('can_add_members_same_level: true');
  if (cfg.can_open_member_profiles === true) parts.push('can_open_member_profiles: true');
  else if (cfg.can_open_member_profiles === false) parts.push('can_open_member_profiles: false');
  if (cfg.open_member_profiles_to_own_group === true) {
    parts.push('open_member_profiles_to_own_group: true');
  } else if (cfg.open_member_profiles_to_own_group === false) {
    parts.push('open_member_profiles_to_own_group: false');
  }
  if (cfg.can_open_member_profiles_same_level === true) {
    parts.push('can_open_member_profiles_same_level: true');
  }
  if (cfg.can_view_group_members === true) parts.push('can_view_group_members: true');
  else if (cfg.can_view_group_members === false) parts.push('can_view_group_members: false');
  if (cfg.view_group_members_in_own_group === true) {
    parts.push('view_group_members_in_own_group: true');
  } else if (cfg.view_group_members_in_own_group === false) {
    parts.push('view_group_members_in_own_group: false');
  }
  if (cfg.can_view_group_members_same_level === true) {
    parts.push('can_view_group_members_same_level: true');
  }
  if (cfg.can_view_member_details === true) parts.push('can_view_member_details: true');
  else if (cfg.can_view_member_details === false) parts.push('can_view_member_details: false');
  if (cfg.view_member_details_in_own_group === true) {
    parts.push('view_member_details_in_own_group: true');
  } else if (cfg.view_member_details_in_own_group === false) {
    parts.push('view_member_details_in_own_group: false');
  }
  if (cfg.can_view_member_details_same_level === true) {
    parts.push('can_view_member_details_same_level: true');
  }
  if (cfg.can_view_member_access === true) parts.push('can_view_member_access: true');
  else if (cfg.can_view_member_access === false) parts.push('can_view_member_access: false');
  if (cfg.view_member_access_in_own_group === true) {
    parts.push('view_member_access_in_own_group: true');
  } else if (cfg.view_member_access_in_own_group === false) {
    parts.push('view_member_access_in_own_group: false');
  }
  if (cfg.can_view_member_access_same_level === true) {
    parts.push('can_view_member_access_same_level: true');
  }
  if (cfg.can_view_derived_groups === true) parts.push('can_view_derived_groups: true');
  else if (cfg.can_view_derived_groups === false) parts.push('can_view_derived_groups: false');
  if (cfg.can_edit_user_data === false) parts.push('can_edit_user_data: false');
  else if (cfg.can_edit_user_data === true) parts.push('can_edit_user_data: true');
  if (cfg.can_edit_sign_in === false) parts.push('can_edit_sign_in: false');
  else if (cfg.can_edit_sign_in === true) parts.push('can_edit_sign_in: true');
  if (cfg.can_grant_platform_access === false) parts.push('can_grant_platform_access: false');
  else if (cfg.can_grant_platform_access === true) parts.push('can_grant_platform_access: true');
  if (cfg.can_add_person_links === false) parts.push('can_add_person_links: false');
  else if (cfg.can_add_person_links === true) parts.push('can_add_person_links: true');
  if (cfg.can_remove_person_links === false) parts.push('can_remove_person_links: false');
  else if (cfg.can_remove_person_links === true) parts.push('can_remove_person_links: true');
  if (cfg.can_edit_person_links === false) parts.push('can_edit_person_links: false');
  else if (cfg.can_edit_person_links === true) parts.push('can_edit_person_links: true');
  if (cfg.can_add_associations === false) parts.push('can_add_associations: false');
  else if (cfg.can_add_associations === true) parts.push('can_add_associations: true');
  if (cfg.can_remove_associations === false) parts.push('can_remove_associations: false');
  else if (cfg.can_remove_associations === true) parts.push('can_remove_associations: true');
  if (cfg.can_edit_student_information === false) parts.push('can_edit_student_information: false');
  else if (cfg.can_edit_student_information === true) parts.push('can_edit_student_information: true');
  if (cfg.can_add_credentials === false) parts.push('can_add_credentials: false');
  else if (cfg.can_add_credentials === true) parts.push('can_add_credentials: true');
  if (cfg.can_edit_credentials === false) parts.push('can_edit_credentials: false');
  else if (cfg.can_edit_credentials === true) parts.push('can_edit_credentials: true');
  if (cfg.can_remove_credentials === false) parts.push('can_remove_credentials: false');
  else if (cfg.can_remove_credentials === true) parts.push('can_remove_credentials: true');
  if (cfg.can_add_attachments === false) parts.push('can_add_attachments: false');
  else if (cfg.can_add_attachments === true) parts.push('can_add_attachments: true');
  if (cfg.can_edit_attachments === false) parts.push('can_edit_attachments: false');
  else if (cfg.can_edit_attachments === true) parts.push('can_edit_attachments: true');
  if (cfg.can_remove_attachments === false) parts.push('can_remove_attachments: false');
  else if (cfg.can_remove_attachments === true) parts.push('can_remove_attachments: true');
  if (cfg.can_add_ugp_properties === false) parts.push('can_add_ugp_properties: false');
  else if (cfg.can_add_ugp_properties === true) parts.push('can_add_ugp_properties: true');
  if (cfg.can_edit_ugp_properties === false) parts.push('can_edit_ugp_properties: false');
  else if (cfg.can_edit_ugp_properties === true) parts.push('can_edit_ugp_properties: true');
  if (cfg.can_remove_ugp_properties === false) parts.push('can_remove_ugp_properties: false');
  else if (cfg.can_remove_ugp_properties === true) parts.push('can_remove_ugp_properties: true');
  if (cfg.can_view_form === true) parts.push('can_view_form: true');
  if (cfg.can_create_submission === true) parts.push('can_create_submission: true');
  if (cfg.can_view_submission === true) parts.push('can_view_submission: true');
  if (cfg.can_edit_draft === true) parts.push('can_edit_draft: true');
  if (cfg.can_edit_submission === true) parts.push('can_edit_submission: true');
  if (cfg.can_delete_submission === true) parts.push('can_delete_submission: true');
  if (cfg.can_view_question_answers === true) parts.push('can_view_question_answers: true');
  if (cfg.can_update_points === true) parts.push('can_update_points: true');
  if (cfg.collaboration && typeof cfg.collaboration === 'object') {
    const col = cfg.collaboration;
    const chat = col.chat || {};
    const chatBits = [];
    ['public', 'internal', 'private'].forEach((vis) => {
      const v = chat[vis] || {};
      const on = ['read', 'write', 'edit'].filter((k) => v[k] === true);
      if (on.length) chatBits.push(`${vis}:${on.join('/')}`);
    });
    if (chatBits.length) parts.push(`chat: ${chatBits.join(', ')}`);
    const flagOn = Object.entries(col.flags || {})
      .filter(([, v]) => v === true)
      .map(([k]) => k);
    if (flagOn.length) parts.push(`flags: ${flagOn.join('/')}`);
    if (col.admin?.viewAllPrivateMessages === true) parts.push('viewAllPrivateMessages: true');
  }
  if (cfg.custom_forms_submission_scope && cfg.custom_forms_submission_scope !== 'own_profile_only') {
    parts.push(`custom_forms_submission_scope: ${cfg.custom_forms_submission_scope}`);
  }
  if (cfg.can_edit_name === false) parts.push('can_edit_name: false');
  else if (cfg.can_edit_name === true) parts.push('can_edit_name: true');
  if (cfg.can_update_password === false) parts.push('can_update_password: false');
  else if (cfg.can_update_password === true) parts.push('can_update_password: true');
  if (cfg.can_create_periods_all_organizations === true) {
    parts.push('can_create_periods_all_organizations: true');
  }
  if (cfg.can_edit_periods_all_organizations === true) {
    parts.push('can_edit_periods_all_organizations: true');
  }
  if (cfg.can_remove_periods_all_organizations === true) {
    parts.push('can_remove_periods_all_organizations: true');
  }
  if (cfg.can_create_periods_own_organization === true) {
    parts.push('can_create_periods_own_organization: true');
  }
  if (cfg.can_edit_periods_own_organization === true) {
    parts.push('can_edit_periods_own_organization: true');
  }
  if (cfg.can_remove_periods_own_organization === true) {
    parts.push('can_remove_periods_own_organization: true');
  }
  if (cfg.forms_view_scope) parts.push(`forms_view_scope: ${cfg.forms_view_scope}`);
  if (cfg.forms_taxonomy_scope && cfg.forms_taxonomy_scope !== 'own_organization') {
    parts.push(`forms_taxonomy_scope: ${cfg.forms_taxonomy_scope}`);
  }
  if (cfg.work_samples_view_scope) parts.push(`work_samples_view_scope: ${cfg.work_samples_view_scope}`);
  if (cfg.can_create_forms === true) parts.push('can_create_forms: true');
  if (cfg.can_edit_forms === true) parts.push('can_edit_forms: true');
  if (cfg.can_delete_forms === true) parts.push('can_delete_forms: true');
  if (cfg.can_assign_forms === true) parts.push('can_assign_forms: true');
  if (cfg.can_assign_work_sample_forms === true) parts.push('can_assign_work_sample_forms: true');
  if (cfg.can_curate_work_samples === true) parts.push('can_curate_work_samples: true');
  if (cfg.can_remove_work_samples === true) parts.push('can_remove_work_samples: true');
  if (cfg.can_submit_portfolios === true) parts.push('can_submit_portfolios: true');
  if (cfg.can_review_portfolios === true) parts.push('can_review_portfolios: true');
  if (cfg.notify_on_inquiry === true) parts.push('notify_on_inquiry: true');

  const layout = getUserProfileViewLayoutFromConfig(cfg);
  if (layout?.columns?.length) parts.push(`layout: ${layout.columns.length} column(s)`);

  return parts.length ? parts.join(' · ') : 'page access only';
}

export { getUserProfileViewLayoutFromConfig };
